import { useCallback, useMemo } from 'react';

import { useApolloClient } from '@apollo/client';
import { getLoyaltyServiceServiceMode } from 'features/offer/utils/service-mode';

import {
  LoyaltyServiceBestPromotionDocument,
  useLoyaltyServiceBestPromotionQuery,
} from 'generated/graphql-gateway';
import { useServiceModeLive } from 'state/service-mode';

import { BestPromotionResult, BestPromotionSourceArgs, shouldClearActivateParam } from '../types';
import {
  buildBestPromotionInput,
  mapAdapterEntriesToServiceEntries,
} from '../utils/map-best-promotion-input';

export function useLoyaltyServiceBestPromotion({
  enabled,
  cartEntries,
  storeId,
  skipStoreDayPartHours,
  onActivateResolved,
}: BestPromotionSourceArgs): BestPromotionResult {
  const serviceMode = getLoyaltyServiceServiceMode(useServiceModeLive());
  const apolloClient = useApolloClient();

  const input = useMemo(
    () =>
      buildBestPromotionInput({
        cartEntries: cartEntries?.length
          ? mapAdapterEntriesToServiceEntries(cartEntries)
          : undefined,
        serviceMode,
        storeId,
        skipStoreDayPartHours,
      }),
    [cartEntries, serviceMode, storeId, skipStoreDayPartHours]
  );

  const { data } = useLoyaltyServiceBestPromotionQuery({
    variables: { input },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
    onCompleted: completed => {
      if (shouldClearActivateParam(completed?.loyaltyServiceBestPromotion)) {
        onActivateResolved?.();
      }
    },
  });

  const refetchPromotion = useCallback(async () => {
    await apolloClient.refetchQueries({ include: [LoyaltyServiceBestPromotionDocument] });
  }, [apolloClient]);

  const promotion = data?.loyaltyServiceBestPromotion;
  const cms = promotion?.cmsContent;

  return {
    promotionId: promotion?.id,
    mustActivate: promotion?.mustActivate,
    userActivatedAt: promotion?.userActivatedAt,
    content: cms
      ? {
          name: cms.name,
          loyaltyEngineId: cms.loyaltyEngineId,
          promoMessage: cms.promoMessage,
          promoMessageBlock: cms.promoMessageBlock?.locale,
          activatedPromoMessageBlock: cms.activatedPromoMessageBlock?.locale,
        }
      : null,
    refetchPromotion,
  };
}
